/**
 * V108-8-13 — a CI gate step whose runs arrive but don't count yet (progress `ci_trust`, core enablers/ci-trust.ts).
 * One line, one action, the same on Setup's step row, Today's next-step strip and the onboarding coverage view:
 *
 *   untrusted     "We received a signed run from <repo> · <workflow> · <branch>"  → Trust it (owner / admin),
 *                 which asks to confirm the exact values first; anyone else → Ask an admin (AskAdmin)
 *   pull_request  only pull-request runs so far → Open pull requests on GitHub (merge first)
 *   trusted       "Trusted. The next run on <branch> counts." → Run it on GitHub
 *   not_counted   results arrived but none is signed by a trusted workflow → the step's own steps
 *
 * "Trust it" sends only the observation id; the server creates the CI identity from the values GitHub attested
 * (POST …/settings/ci-identities/observations/:id/trust). A trusted workflow covers every system in the
 * workspace, and the confirm says so.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@aigovops/design-system';
import type { CiTrustProgress, CiTrustSuggestion } from '@aigovops/core';
import { ApiError } from '../../lib/api';
import { dismissObservedRun, trustObservedRun, type EnablerItem } from '../../lib/setup-api';
import { AskAdmin } from '../AskAdmin';
import { ExtLink, Glyph, State } from './parts';

const fileOf = (path: string | null) => (path ? path.slice(path.lastIndexOf('/') + 1) : '');

/** The one line for a step's CI trust state (plain words, no internal identifiers). */
export function ciTrustLine(t: CiTrustProgress): string {
  // V108-8-20: a done step whose NEW signed runs don't count (a new, renamed or moved workflow or branch).
  if (t.step_done) {
    if (t.status === 'pull_request') return `New pull-request runs from ${t.repository} · ${fileOf(t.workflow_path)}. Merge the pull request; its first run on your default branch can then be trusted here.`;
    if (t.status === 'not_counted') return t.unsigned ? "New CI results arrived unsigned, so they don't count." : "New CI results aren't signed by a trusted workflow, so they don't count.";
    return (t.suggestions?.length ?? 0) > 1 ? `We received signed runs from ${t.suggestions!.length} workflows or branches that don't count yet`
      : `We received signed runs from ${t.repository} · ${fileOf(t.workflow_path)} · ${t.branch} that don't count yet`;
  }
  switch (t.status) {
    case 'untrusted': return (t.suggestions?.length ?? 0) > 1 ? `We received signed runs from ${t.suggestions!.length} repositories` : `We received a signed run from ${t.repository} · ${fileOf(t.workflow_path)} · ${t.branch}`;
    case 'pull_request': return `Only pull-request runs from ${t.repository} so far. Merge it, then trust its first run on your default branch here.`;
    case 'trusted': return `Trusted. The next run on ${t.branch} counts.`;
    case 'not_counted':
    default: return t.unsigned ? "CI results arrived unsigned, so they don't count yet." : "CI results arrived, but none is signed by a trusted workflow, so they don't count yet.";
  }
}

/** The status chip before the line. */
export function CiTrustState({ t }: { t: CiTrustProgress }) {
  if (t.step_done && t.status !== 'pull_request') return <State tone="attention" glyph="alert">{t.unsigned ? 'New runs unsigned' : "New runs don't count"}</State>;
  if (t.status === 'trusted') return <State tone="progress" glyph="clock">Waiting for the next run</State>;
  if (t.status === 'pull_request') return <State tone="progress" glyph="clock">Waiting for a merge</State>;
  return <State tone="attention" glyph="alert">{t.unsigned ? 'Unsigned' : "Doesn't count yet"}</State>;
}

/** The CLI flags that sign a result (`--sign-result` needs the run's identity: verification.ts refuses it alone). */
export const SIGN_FLAGS = '--identity --sign-result';

/** V108-8-20: unsigned CI results are fixed by signing them, not by trusting a workflow. */
export function SigningHint() {
  return <p className="su-note" data-part="ci-signing">
    Sign each run: give the workflow job <code className="su-mono">permissions: id-token: write</code> and run the check with <code className="su-mono">{SIGN_FLAGS}</code>.
    The first signed run on your default branch can then be trusted here.
  </p>;
}

type Suggestion = CiTrustSuggestion;
/** The suggestions of an untrusted step (older Workers send only the top-level fields). */
export function suggestionsOf(t: CiTrustProgress): Suggestion[] {
  if (t.suggestions?.length) return t.suggestions;
  if (!t.observation_id || !t.repository) return [];
  return [{ observation_id: t.observation_id, repository: t.repository, workflow_path: t.workflow_path, branch: t.branch, last_seen_at: t.last_seen_at, seen_count: t.seen_count,
    github_url: t.github_url, owner: t.owner ?? t.repository.split('/')[0]!, owner_known: t.owner_known ?? false }];
}
const nameOf = (repository: string | null) => (repository ? repository.slice(repository.indexOf('/') + 1) : '');

export function CiTrustAction({ slug, step, approver, primary = false, viewerId, onChanged, announce, compact = false, all = false }: {
  slug: string; step: Pick<EnablerItem, 'id' | 'progress' | 'subject_key'>; approver: boolean;
  /** This action is the surface's one filled primary (only ever for an account the workspace connected). */
  primary?: boolean; viewerId?: string | null;
  onChanged: () => Promise<unknown>; announce: (m: string) => void;
  /** The strip: compact buttons. */
  compact?: boolean;
  /** Review M-1 (b): Setup's step row lists every observed repository with its own action; other surfaces show the top one. */
  all?: boolean;
}) {
  const t = step.progress.ci_trust;
  if (!t) return null;
  const variant = primary ? 'primary' : undefined;
  // Flow review (Blockers): every state keeps one action even when a value is missing.
  const stepHref = `/w/${encodeURIComponent(slug)}/app/setup?step=${encodeURIComponent(step.id)}#steps`;
  const stepLink = <Link className="su-btn" data-variant={variant} to={stepHref}>See the steps</Link>;
  const repoUrl = t.repository ? `https://github.com/${t.repository}` : null;
  if (t.status === 'trusted') { const href = t.github_url ?? (repoUrl ? `${repoUrl}/actions` : null); return href ? <ExtLink href={href} variant={variant}>Run it on GitHub</ExtLink> : stepLink; }
  if (t.status === 'pull_request') { const href = t.github_url ?? (repoUrl ? `${repoUrl}/pulls` : null); return href ? <ExtLink href={href} variant={variant}>Open pull requests on GitHub</ExtLink> : stepLink; }
  if (t.status === 'not_counted') return stepLink;
  const list = suggestionsOf(t);
  if (!list.length) return <Link className="su-btn" data-variant={variant} to={`/w/${encodeURIComponent(slug)}/app/settings/security#ci-identities`}>Open CI identities</Link>;
  if (!all) {
    const top = list[0]!;
    return <>
      <SuggestionAction slug={slug} stepId={step.id} s={top} approver={approver} primary={primary && top.owner_known} compact={compact} viewerId={viewerId} onChanged={onChanged} announce={announce} connected={t.connected_accounts ?? []} doneStep={!!t.step_done} />
      {list.length > 1 ? <Link className="su-linkish" to={stepHref}>{`${list.length - 1} more in Setup`}</Link> : null}
    </>;
  }
  return <ul className="su-trust-list" data-part="ci-trust-suggestions">
    {list.map((s, i) => (
      <li key={s.observation_id ?? i} data-part="ci-trust-suggestion" data-known={s.owner_known ? '' : undefined}>
        {list.length > 1 && <p className="su-row-sub"><span className="su-mono" style={{ color: 'var(--text)' }}>{s.repository}</span><span>{`· ${fileOf(s.workflow_path)} · ${s.branch}`}</span></p>}
        <SuggestionAction slug={slug} stepId={step.id} s={s} approver={approver} primary={primary && i === 0 && s.owner_known} compact={compact} viewerId={viewerId} onChanged={onChanged} announce={announce} connected={t.connected_accounts ?? []} doneStep={!!t.step_done} />
      </li>
    ))}
  </ul>;
}

/** Review M-1 (a): an account the workspace hasn't connected, said before any action (never a one-click primary). */
export function ForeignOwnerWarning({ owner }: { owner: string }) {
  return <p className="su-warn" data-part="ci-trust-foreign" style={{ margin: 0 }}><Glyph kind="alert" /><span>From an account this workspace hasn't connected: <span className="su-mono">{owner}</span></span></p>;
}

function SuggestionAction({ slug, stepId, s, approver, primary, compact, viewerId, onChanged, announce, connected = [], doneStep = false }: {
  slug: string; stepId: string; s: Suggestion; approver: boolean; primary: boolean; compact: boolean; viewerId?: string | null;
  /**
   * V108-8-20 (flow review High): on a done step the run is usually the workspace's own (a new branch, a removed rule):
   * for a connected account the dismiss reads "Don't count this branch" (it hides exactly this workflow + branch).
   */
  doneStep?: boolean;
  /** The accounts this workspace connected, shown next to a suspect one (flow review High). */
  connected?: readonly string[];
  onChanged: () => Promise<unknown>; announce: (m: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<'trust' | 'dismiss' | null>(null);
  const [ours, setOurs] = useState(false);
  // Flow / a11y review: "Not ours" is permanent, so it asks once before hiding.
  const [hiding, setHiding] = useState(false);
  const branchOnly = doneStep && s.owner_known;
  const [error, setError] = useState<{ text: string; settings: boolean; local?: boolean } | null>(null);
  const confirmH = useRef<HTMLHeadingElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const oursBox = useRef<HTMLInputElement>(null);
  useEffect(() => { if (confirming) confirmH.current?.focus(); }, [confirming]);
  const what = `${s.repository} · ${fileOf(s.workflow_path)} · ${s.branch}`;
  const warning = s.owner_known ? null : <ForeignOwnerWarning owner={s.owner} />;
  if (!approver) {
    return <>{warning}<AskAdmin slug={slug} compact verb="trust it" what="trusts CI workflows" exclude={viewerId ?? null} subject={`Trust ${what} in GAC Pro`} /></>;
  }
  const refocus = (host: HTMLElement | null) => window.setTimeout(() => {
    // A11y review (High): the confirm goes away; focus moves to the step that holds it, never to <body>.
    const target = host && document.contains(host) ? (host.querySelector<HTMLElement>('.su-row-title, .su-next-title') ?? host) : document.getElementById('su-steps-h');
    if (target) { if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1'); target.focus(); }
  }, 0);
  const hostOf = () => (confirmH.current ?? opener.current)?.closest<HTMLElement>('li.su-row, [data-part="status"], .su-next') ?? null;
  const cancel = () => { setConfirming(false); setError(null); setOurs(false); window.setTimeout(() => opener.current?.focus(), 0); };
  const fail = (err: unknown, fallback: string) => {
    const status = err instanceof ApiError ? err.status : 0;
    const body = err instanceof ApiError && err.body && typeof err.body === 'object' ? err.body as { error?: string; code?: string } : {};
    setError({ text: body.error ?? fallback, settings: status === 409 && body.code !== 'owner_not_confirmed' });
  };
  const trust = async () => {
    if (busy || !s.observation_id) return;
    if (!s.owner_known && !ours) { setError({ text: `Tick "${s.owner} is our GitHub account" to continue.`, settings: false, local: true }); oursBox.current?.focus(); return; }
    setBusy('trust'); setError(null);
    announce('Trusting the workflow…');
    const host = hostOf();
    try {
      await trustObservedRun(slug, s.observation_id, s.owner_known ? undefined : s.owner);
      setConfirming(false);
      announce(`Trusted. The next run on ${s.branch} counts.`);
      await onChanged();
      refocus(host);
    } catch (err) { fail(err, 'The workflow could not be trusted. Try again.'); } finally { setBusy(null); }
  };
  const notOurs = async () => {
    if (busy || !s.observation_id) return;
    setBusy('dismiss'); setError(null);
    const host = hostOf();
    try {
      await dismissObservedRun(slug, s.observation_id);
      announce(branchOnly ? `GAC Pro won't suggest ${fileOf(s.workflow_path)} · ${s.branch} again. Trusted runs still count.` : `Hidden. GAC Pro won't suggest ${s.repository} again.`);
      await onChanged();
      refocus(host);
    } catch (err) { fail(err, 'That did not save. Try again.'); } finally { setBusy(null); }
  };
  if (hiding) {
    return <div className="su-inline-form" role="group" aria-label={`Hide ${s.repository} from suggestions`} data-part="ci-trust-hide"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setHiding(false); } }}>
      <p className="su-row-sub" style={{ margin: 0 }}>{branchOnly ? <><span>Stop suggesting</span><span className="su-mono" style={{ color: 'var(--text)' }}>{`${fileOf(s.workflow_path)} · ${s.branch}`}</span><span>for good? Its runs keep not counting.</span></>
        : <><span>Hide</span><span className="su-mono" style={{ color: 'var(--text)' }}>{s.repository}</span><span>from suggestions for good?</span></>}</p>
      <div className="su-acts">
        <Button compact autoFocus onClick={() => void notOurs()} aria-disabled={busy === 'dismiss' || undefined}>{busy === 'dismiss' ? 'Hiding…' : branchOnly ? 'Stop suggesting it' : 'Hide it'}</Button>
        <Button variant="ghost" compact onClick={() => setHiding(false)}>Cancel</Button>
      </div>
      {error ? <p className="su-err" role="alert">{error.text}</p> : null}
    </div>;
  }
  if (!confirming) {
    return <>
      {warning}
      <span className="su-acts">
        <Button ref={opener} variant={primary ? 'primary' : undefined} compact={compact || undefined} onClick={() => setConfirming(true)}>
          {s.owner_known ? 'Trust it' : 'Review'}<span className="su-sr">{`: ${what}`}</span>
        </Button>
        <Button variant="ghost" compact={compact || undefined} onClick={() => setHiding(true)}>{branchOnly ? "Don't count this branch" : 'Not ours'}<span className="su-sr">{`: ${what}`}</span></Button>
      </span>
      {error && !confirming ? <p className="su-err" role="alert">{error.text}</p> : null}
    </>;
  }
  const hId = `su-trust-h-${stepId}-${s.observation_id}`;
  return (
    <div className="su-inline-form" role="group" aria-labelledby={hId} data-esc-owner="" data-part="ci-trust-confirm" aria-busy={busy === 'trust' || undefined}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancel(); } }}>
      <h5 id={hId} ref={confirmH} tabIndex={-1} className="su-row-title">Trust exactly this workflow?</h5>
      {warning}
      <dl className="su-kv">
        <dt>Account</dt><dd className="su-mono" data-part="ci-trust-owner">{s.owner}</dd>
        {!s.owner_known && connected.length > 0 && <><dt>Your connected account{connected.length > 1 ? 's' : ''}</dt><dd className="su-mono" data-part="ci-trust-connected">{connected.join(', ')}</dd></>}
        <dt>Repository</dt><dd><strong>{nameOf(s.repository)}</strong></dd>
        <dt>Workflow file</dt><dd className="su-mono">{s.workflow_path}</dd>
        <dt>Branch</dt><dd className="su-mono">{s.branch}</dd>
        <dt>CI</dt><dd>GitHub Actions</dd>
      </dl>
      {/* Review M-1: an account nobody connected needs an explicit "this is ours" before anything is trusted. */}
      {!s.owner_known && <label className="su-radio" data-part="ci-trust-ours">
        <input ref={oursBox} type="checkbox" checked={ours} aria-required="true" aria-describedby={error?.local ? `${hId}-err` : undefined} aria-invalid={error?.local && !ours ? true : undefined}
          onChange={(e) => { setOurs(e.currentTarget.checked); setError(null); }} />
        <span><span className="su-mono">{s.owner}</span> is our GitHub account <span className="su-hint">(required to trust)</span></span>
      </label>}
      {error?.local && <p className="su-err" id={`${hId}-err`}>{error.text}</p>}
      <p className="su-hint">{`Counts for every system in this workspace · ${s.branch} only · stop trusting it any time in Settings → Security`}</p>
      {error && !error.local && <p className="su-err" role="alert">{error.text}{error.settings ? <> <Link to={`/w/${encodeURIComponent(slug)}/app/settings/security#ci-identities`}>Open CI identities</Link></> : null}</p>}
      <div className="su-acts">
        {/* Critics (Medium): no filled button until a foreign account is confirmed as ours. */}
        <Button variant={s.owner_known || ours ? 'primary' : 'secondary'} compact onClick={() => void trust()} aria-disabled={busy === 'trust' || undefined}>{busy === 'trust' ? 'Trusting…' : 'Yes, trust it'}</Button>
        <Button variant="ghost" compact onClick={cancel}>Cancel</Button>
      </div>
    </div>
  );
}
